/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable no-alert */
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { gql } from "graphql-tag";
import { get, omit } from "lodash";
import moment from "moment";
import React, { useState, useEffect } from "react";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { FormControl, InputLabel, Select, MenuItem, Grid } from "@mui/material";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Column, FormRow } from "components/common/html";
import { Button, DropDownButton } from "components/haven-design/buttons";
import logger from "services/logger";
import { parseDumbInterestForm } from "utils/interest";
import { getInterestListName, FormData, createInterest as createInterestType, createInterestVariables, InterestInput, UserRole } from "types/gql-types";
import NewForm from "../interests-form";

type KeyOfInterestInput = keyof InterestInput;
type UnderscoredInterestInput = Record<string, InterestInput[KeyOfInterestInput]>;

const styles = {
  container: {
    padding: 20,
  },
  sidebarContainer: {
    minHeight: 450,
  },

  header: {
    marginTop: 0,
    textAlign: "left",
  },

  topPadding: {
    paddingTop: 16,
  },

  tableContainer: {
    minHeight: 450,
  },
  subheader: {
    boxShadow: `0 0 0 1px rgba(63,63,68,0.02),
                0 1px 3px rgba(63, 63, 68, 0.1),
                0 1px 2px rgba(0,0,0,0.02)`,
    backgroundColor: "white",
    minHeight: 58,
  },
} as const;

const QUERY_FORM_DATA = gql`
  query formData($interestListId: ID!) {
    applicationFormMetadata(interestListId: $interestListId, source: APP) {
      schema {
        name
        label
        options {
          label
          value
        }
      }
    }
  }
`;

const CREATE_NEW_INTEREST = gql`
  mutation createInterest($listId: String!, $data: InterestInput!) {
    createInterest(listId: $listId, data: $data) {
      id
      manuallyEntered
    }
  }
`;

const INTEREST_LIST_QUERY = gql`
  query getInterestListName($interestListId: ID!) {
    interestList(id: $interestListId) {
      id
      name
    }
  }
`;

const NewInterest: React.FC = () => {
  const navigate = useNavigate();
  const [isFormReady, setIsFormReady] = useState(false);
  const [householdCount, setHouseholdCount] = useState(0);
  const newDateTime = moment();
  const [date, setDate] = useState(newDateTime);
  const [dateTime, setDateTime] = useState(newDateTime);
  const [time, setTime] = useState(newDateTime);
  const [submitBtnIndex, setSubmitBtnIndex] = useState(0);

  useEffect(() => {
    const currentDate = date.clone();
    currentDate.set({
      hour: time.get("hour"),
      minute: time.get("minute"),
      second: 0,
      millisecond: 0,
    });
    setDateTime(currentDate);
  }, [date, time]);

  const { listId: interestListId } = useParams() as { listId?: string };
  if (!interestListId) return null;
  const query = useQuery<FormData>(QUERY_FORM_DATA, { variables: { interestListId } });
  const interestListQuery =
    useQuery<getInterestListName>(INTEREST_LIST_QUERY, { variables: { interestListId } });

  const [createInterest, mutation] =
    useMutation<createInterestType, createInterestVariables>(CREATE_NEW_INTEREST);
  const onResetState = () => {
    setIsFormReady(() => false);
    setHouseholdCount(() => 0);
    setDate(() => newDateTime);
    setTime(() => newDateTime);
  };

  const isToday = (dateValue:moment.Moment) => dateValue.isSame(newDateTime, "day") &&
    dateValue.isSame(newDateTime, "month") &&
    dateValue.isSame(newDateTime, "year");
  const timeError = isToday(date) && moment().isBefore(time);
  const handleCancel = () => navigate(-1);
  const handleDateChange = (currentMoment: string | moment.Moment | null | Date) => {
    const DateToSet = moment(currentMoment);

    setDate(DateToSet);

    if (isToday(DateToSet) && moment().isBefore(time)) {
      setTime(moment());
    }
  };
  const handleTimeChange = (currentMoment: string | moment.Moment | null | Date) =>
    setTime(moment(currentMoment));
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let data = parseDumbInterestForm(event.target) as UnderscoredInterestInput;
    data.submittedAt = Number(data.submittedAt); // parse string
    data = omit(data, "temp-input");

    createInterest({ variables: { listId: interestListId, data } })
      .then(() => {
        if (submitBtnIndex === 1) return onResetState();

        navigate(`/dash/manager/lists/${interestListId}`);
        return null;
      })
      .catch((err) => {
        logger.sendError(err);
        alert("There was a problem creating the application. Please try again later.");
      });
  };

  const formSchema = get(query, ["data", "applicationFormMetadata", "schema"], []);
  return (
    <div style={styles.container} className="legacy-bs-override">
      <FormRow>
        <Column width={2} />
        <Column width={8}>
          <div className="card" style={styles.tableContainer}>
            <h3 style={styles.header}>{!interestListQuery.loading && (
              <span>{interestListQuery.data?.interestList.name} Application</span>
            )}</h3>
            {!isFormReady && (
              <div style={styles.topPadding}>
                <div>
                  <Grid container spacing={2}>
                    <Grid size={{ md: 3 }}>
                      <DatePicker
                        slotProps={{
                          textField: {
                            variant: "outlined",
                            label: "Application Date",
                          },
                        }}
                        value={date.toDate()}
                        disableFuture
                        onChange={handleDateChange}
                        format="MM/dd/yyyy"
                      />
                    </Grid>
                    <Grid size={{ md: 3 }}>
                      <TimePicker
                        slotProps={{
                          textField: {
                            variant: "outlined",
                            label: "Application Time",
                            error: timeError,
                            helperText: timeError && "Time cannot be in the future.",
                          },
                        }}
                        value={time.toDate()}
                        onChange={handleTimeChange}
                        format="hh:mm a"
                      />
                    </Grid>
                  </Grid>
                </div>

                <FormControl margin="dense" variant="outlined" style={{ minWidth: 150, marginTop: 16, marginBottom: 16 }}>
                  <InputLabel id="household-size-label">Household size</InputLabel>
                  <Select
                    margin="dense"
                    labelId="household-size-label"
                    id="household-size"
                    value={householdCount}
                    onChange={(event) => setHouseholdCount(Number(event.target.value))}
                    label="Household size"
                    data-cy="household-size-select"
                  >
                    <MenuItem value={0} disabled>Choose</MenuItem>
                    {[...Array(12).keys()].map((idx: number) => (
                      <MenuItem key={`hh-member-${idx}`} value={idx + 1}>{idx + 1}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <br />
                <Button
                  type="button"
                  color="secondary"
                  size="L"
                  marginRight
                  onClick={() => history.back()}
                  data-cy="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  size="L"
                  disabled={householdCount === 0 || timeError || !dateTime}
                  onClick={() => setIsFormReady(true)}
                  data-cy="next-button"
                >
                  Next
                </Button>
              </div>
            )}

            {isFormReady && (
              <form onSubmit={handleSubmit} style={{ display: "grid" }}>
                <NewForm
                  formData={{
                    householdCount,
                    createType: "paper",
                    submittedAt: dateTime.unix(),
                    isComplete: true,
                    manuallyEntered: true,
                    contactMethodPhone: true,
                    contactMethodSms: true,
                    contactMethodEmail: true,
                    contactMethodMail: true,
                  }}
                  validationSchema={formSchema}
                />
                <div className="clearfix">
                  <div className="pull-right">
                    <Button
                      type="button"
                      color="secondary"
                      size="L"
                      marginRight
                      onClick={handleCancel}
                      data-cy="cancel-button"
                    >
                      Cancel
                    </Button>

                    <DropDownButton
                      type="submit"
                      size="L"
                      color="primary"
                      disabled={mutation.loading}
                      currentOption={submitBtnIndex}
                      onOptionClick={(index) => setSubmitBtnIndex(index)}
                      options={["Submit & Close", "Submit & New"]}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </Column>
        <Column width={2} />
      </FormRow>
    </div>
  );
};

export default authorizeForRole([UserRole.PROPERTY_MANAGER, UserRole.SUPERVISOR], NewInterest);
